import type { caisse as Fr } from '../fr/caisse';

const pl = (n: number, un: string, plusieurs: string) => `${n} ${n === 1 ? un : plusieurs}`;

/** « caisse » in English — typed on the French: a missing sentence does not compile. */
export const caisse: typeof Fr = {
  etatsJour: { loue: 'rented', reserve: 'booked', entretien: 'in the garage', libre: 'available' },
  jours: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  modes: {
    especes: 'Cash', cheque: 'Cheque', virement: 'Bank transfer', carte: 'Card', traite: 'Bill of exchange',
  },
  dossierIntrouvable: 'This rental cannot be found — the payment will not be linked to anything.',

  titre: 'Cash register',
  sousTitre: (devise) => `One drawer per currency · consolidated total in ${devise}`,
  vueJour: 'Day',
  vueMois: 'Month',
  encaisser: 'Take payment',
  operationChange: 'Currency exchange',
  tauxDuJour: 'Today\'s rate',
  reserveGerants: 'Managers only',
  cloturer: 'End-of-day close',

  selecteur: {
    precedent: 'Previous month',
    suivant: 'Next month',
    choisir: 'Choose the month',
    enCours: ' — current',
    aVenir: ' — upcoming',
    revenir: 'Back to the current month',
  },

  moisAVenir: {
    titre: 'Upcoming month — what is already booked',
    gras1: (mois) => `Nothing has been collected or spent in ${mois} yet.`,
    texte1: ' The amounts below that are not zero do not come from this month: the ',
    gras2: 'cash balance',
    texte2: ' is what the drawer holds today, ',
    gras3: '“what we are owed”',
    texte3: ' is what is unpaid to date, and ',
    gras4: '“forecast this month”',
    texte4: (mois) => ` is what the rentals already booked will bring in — none of this is money received in ${mois}. A rental this month that was already paid in advance is counted in the cash register of the month the money came in, and the vehicle concerned says so.`,
  },

  bilan: {
    fonds: 'Cash float put in',
    fondsDetail: 'what you put into the cash register this month',
    encaisse: 'Collected',
    depense: 'Spent',
    fondsConsomme: (pct) => `${pct}% of the float used`,
    soldeAujourdhui: 'Cash balance today',
    solde: 'Cash balance',
    maintenantAvant: 'what the drawer holds ',
    maintenantGras: 'now',
    maintenantApres: (mois) => ` — nothing has moved in ${mois} yet`,
    auPremier: 'on the 1st ',
    verseBanque: '− paid into the bank ',
  },

  quiAFaitQuoi: 'Who did what this month',
  colonnesPersonnes: { personne: 'Person', operations: 'Transactions', encaisse: 'Collected', depense: 'Spent' },
  noteNominative: 'Every line carries a name and comes from the audit log: nobody can spend without their name staying on record. The float is the sum of the month\'s cash added.',

  rendement: {
    titre: (mois) => `What each vehicle brought in — ${mois}`,
    legende: { louee: 'Rented', reservee: 'Booked', entretien: 'Maintenance', libre: 'Available' },
    prevu: 'Forecast this month',
    genere: 'Revenue this month',
    prevuDetail: 'rentals already booked — nothing is collected',
    genereDetail: (actives, total) => `${actives} of ${pl(total, 'vehicle', 'vehicles')} worked`,
    encaissePourVoitures: 'Collected for these vehicles',
    dontAvance: (m) => `of which ${m} in advance for the following months`,
    dontBanque: (m) => `of which ${m} at the bank`,
    dejaAvant: (m) => `${m} for this month already collected earlier`,
    encaisseDetail: 'rental money received this month',
    reservePlusTard: 'Booked for later',
    reservePlusTardDetail: 'already committed, beyond this month',
    immobiles: 'Idle vehicles',
    immobilesDetail: 'no day rented or booked',
    parJour: (m) => `${m} / day`,
    joursLoues: (j) => `${j} d rented`,
    joursReserves: (j) => ` · ${j} booked`,
    joursGarage: (j) => ` · ${j} in the garage`,
    locations: (n) => pl(n, 'rental', 'rentals'),
    reserveApres: (j) => `Already booked ${pl(j, 'day', 'days')} after this month — `,
    pasSortie: 'Did not go out all month.',
    encaisseMois: 'Collected this month',
  },

  supplements: {
    titre: (m) => `Extra charges invoiced — ${m}`,
    vide: 'No extra charges this month',
    videTexte: 'Late returns, missing fuel, mileage beyond the allowance and car washes will appear here, with the calculation behind them.',
    colonnes: { supplement: 'Extra charge', calcul: 'Calculation', voiture: 'Vehicle', montant: 'Amount' },
  },
  cautions: {
    titre: (m) => `Deposits — ${m} held`,
    vide: 'No deposit movements',
    colonnes: { mouvement: 'Movement', detail: 'Details', par: 'By', montant: 'Amount' },
    note: 'A deposit is not income: it is a customer\'s money, held. It is taken out of the cash actually available.',
  },

  ouArgent: {
    titre: (mois) => `Where the money went — ${mois}`,
    sorti: 'Out of the cash register',
    sortiDetail: 'to the bank, to petty cash, or spent directly',
    doitAujourdhui: 'What we are owed today',
    doit: 'What we are owed',
    doitDetail: (n, aVenir) => `${pl(n, 'rental', 'rentals')} with a balance due` + (aVenir ? ' · all months combined' : ''),
    avances: 'Advance payments collected',
    avancesDetail: 'already paid, not yet delivered',
    sorties: 'Money out of the main cash register',
    rienSorti: 'Nothing left the cash register this month.',
    colonnesSorties: { date: 'Date', motif: 'Reason', vers: 'To', par: 'By', montant: 'Amount' },
    personne: 'Nobody owes you anything.',
    colonnesDettes: { client: 'Customer', voiture: 'Vehicle', dossier: 'Rental', resteDu: 'Balance due' },
    aCredit: ' · on account',
    parTranches: ' · in instalments',
    avancesTitre: 'Advance payments: collected for rentals not yet delivered',
    aucuneAvance: 'No advance payments outstanding.',
    colonnesAvances: {
      dossier: 'Rental', voiture: 'Vehicle', client: 'Customer', periode: 'Period',
      encaisse: 'Collected', nonLivre: 'Not yet delivered',
    },
    avancesNote: 'This money is in your cash register but it is not earned: the vehicle still has to go out. It is the part of the balance you must not commit.',
  },

  jour: {
    totalConsolide: 'Consolidated total',
    dont: 'of which ',
    futures: ' in future services already collected and ',
    cautions: ' in deposits — ',
    nonDisponibles: 'not available',
    fin: '.',
    disponible: 'Cash actually available',
    toutesPoches: 'all accounts ',
    moinsFutures: '− future services ',
    moinsCautions: '− deposits ',
    mouvements: 'Movements',
    aucunMouvement: 'No movements that day',
    colonnes: {
      heure: 'Time', type: 'Type', libelle: 'Description', mode: 'Method', devise: 'Currency',
      montant: 'Amount', taux: 'Rate', contrevaleur: 'Equivalent', agent: 'Agent',
    },
    resultatChange: (gain, m) => `exchange ${gain ? 'gain' : 'loss'} ${m}`,
    versBanque: '→ bank',
  },
  noteAgent: 'The end-of-day close is reserved for the person in charge. Your payments are final as soon as they are recorded.',

  encaissement: {
    confirmerDoublon: "Yes, this is a second payment",
    libelleContrat: (numero, client) => `Rental balance ${numero} — ${client}`,
    libelleDefaut: 'Rental payment',
    rattache: (numero) => `Payment linked to agreement ${numero}.`,
    enregistre: 'Payment recorded.',
    erreur: 'Error',
    titreContrat: (numero) => `Take payment — agreement ${numero}`,
    titre: 'Take payment',
    resteDu: (m) => `Balance due: ${m}`,
    resteDuTexte: 'This payment will be linked to the rental: the agreement balance and the available cash are updated.',
    nonRattache: 'Payment not linked',
    nonRattacheTexte: 'No rental is selected: this amount will go into the cash register without settling any agreement. To settle a rental, start from the rental or from the “Balances to collect” list.',
    libelle: 'Description',
    sansTaux: (devises) => `No today's rate for ${devises}`,
    sansTauxAvant: (devise) => `Without a rate, this payment cannot be converted into ${devise} and will be refused. A manager enters it in `,
    sansTauxGras: 'Cash register › Today\'s rate',
    sansTauxApres: '.',
    montant: 'Amount',
    devise: 'Currency',
    payeEn: 'Paid by',
    retirer: 'remove',
    conversion: (n, devise, m, taux) => `${n} ${devise} ≈ ${m} (rate ${taux})`,
    ajouterDevise: '+ Add a currency (split payment)',
    banqueTitre: 'Part of it goes to the bank, not the cash register',
    banqueTexte: (liste) => `${liste} — the cash register only holds what can be counted by hand. The rest of the rental is settled the same way: it is the total that counts.`,
    totalEn: (devise) => `Total in ${devise}`,
  },

  change: {
    enregistre: 'Currency exchange recorded.',
    sortie: 'Out of the cash register',
    entree: 'Into the cash register',
    resultat: (gain, m) => `Exchange ${gain ? 'gain' : 'loss'}: ${m}`,
    detail: (sortie, entree) => `Out ${sortie} · in ${entree} (equivalents at today's rate)`,
    bouton: 'Record the exchange',
  },

  cloture: {
    faite: 'Cash register closed.',
    titre: 'End-of-day close — count by currency',
    theorique: 'Expected',
    compte: 'Counted',
    ecart: (m) => `Discrepancy of ${m} — reason required`,
    bouton: 'Close',
  },

  taux: {
    misAJour: 'Rates updated — past movements are not changed.',
    titre: 'Rates applied at the agency',
    texteAvant: 'The rate applied at the agency is not the bank\'s rate. Each rate is kept in history with its effective date, and ',
    texteGras: 'movements already recorded keep their original equivalent',
    texteApres: '.',
    bouton: 'Save the rates',
  },
};
